import type { GeneralizedTaskId } from "@ctrl/controller-rpc";

import { identifyKind } from "@/utils";
import { NANOID_LENGTH, nanoidOfPrefixedId } from "@/utils/nanoid";

// This module must stay free of renderer-only dependencies (Vue components,
// the graph world): it is bundled into the Electron main process through
// `navigate.ts` (`background.ts` → `hotkeys/join` → `useConference` →
// `open-external-url`). The default-parent id encoding lives in
// `@/components/databases` for that reason.

/**
 * Whether a generalized task id designates a native task (`task:…`).
 * Complement of `isBoundTaskId`.
 */
export const isNativeTaskId = (id: GeneralizedTaskId): boolean =>
  identifyKind("task", id);

/**
 * Whether a generalized task id designates a bound task — a graph database
 * row (`object:workspace:table:row`) surfaced as a task. Complement of
 * `isNativeTaskId`.
 *
 * Use these id predicates when the answer must agree with the id itself
 * (routing, pinning, link building, TaskId-only RPC guards); use
 * `isBoundTask` (in `@/task`) when dispatching on a task record, which also
 * carries a `binding` field.
 */
export const isBoundTaskId = (id: GeneralizedTaskId): boolean =>
  identifyKind("object", id);

/**
 * Whether `id` is a generalized task id: a native task (`task:…`) or a graph
 * database row surfaced as a task (`object:workspace:table:row`).
 */
export const isGeneralizedTaskId = (id: string): boolean =>
  isNativeTaskId(id) || isBoundTaskId(id);

/** Whether `id` is a canonical native page id (`page:<nanoid>`). */
export const isPageId = (id: string): boolean =>
  nanoidOfPrefixedId("page", id) !== null;

/**
 * Whether `id` is a well-formed canonical native task id (`task:<nanoid>`).
 * Like `isWellFormedGeneralizedTaskId` but excluding bound tasks — use it to
 * validate untrusted ids for surfaces that only host native tasks (the task
 * screen).
 */
export const isWellFormedTaskId = (id: string): boolean =>
  nanoidOfPrefixedId("task", id) !== null;

/**
 * Whether `id` has the exact textual shape of a custom object id —
 * `object:<table>:<row>` or `object:<workspace>:<table>:<row>` with 21-char
 * nanoid parts (mirrors `Ids.Object.of_string` in the controller).
 */
export const isWellFormedObjectId = (id: string): boolean => {
  const parts = id.split(":");
  return (
    parts[0] === "object" &&
    (parts.length === 3 || parts.length === 4) &&
    parts.slice(1).every((part) => part.length === NANOID_LENGTH)
  );
};

/**
 * Whether `id` has the exact textual shape of a generalized task id. Unlike
 * `isGeneralizedTaskId` (prefix check only), this mirrors the controller's
 * parser, so an id that passes never fatals at the RPC schema boundary. Use
 * it to validate untrusted ids such as URL params: malformed `/tasks/…` deep
 * links (e.g. several ids joined with spaces) used to crash the whole app
 * with a ControllerFatal (FRONTEND-F4Q).
 */
export const isWellFormedGeneralizedTaskId = (id: string): boolean =>
  nanoidOfPrefixedId("task", id) !== null || isWellFormedObjectId(id);
